/**
 * Dock detection (M2 spec §7.2 step 3): the dock completes when the ship stays inside the bay volume, slower than
 * `dock.maxSpeed`, with its nose and roll errors within `dock.maxAlign`, for `dock.holdSeconds` in a row. Leaving
 * the limits resets the hold. Runs every tick on the readout `corridorState` filled: allocation-free (D-014).
 */
import type { DockingConfig } from '../data/m2Schemas'
import type { DockingReadout } from '../space/contracts'

const RAD_TO_DEG = 180 / Math.PI
/** Slack on the hold time: `n × SIM_DT` accumulates rounding error (the same idea as the sim's respawn slack). */
const HOLD_EPS = 1e-6

/** True while the readout is inside every dock limit (in the bay, speed below the limit, nose and roll aligned). */
export function dockConditionsMet(readout: Readonly<DockingReadout>, cfg: Pick<DockingConfig, 'dock'>): boolean {
  const maxDeg = cfg.dock.maxAlign * RAD_TO_DEG
  return (
    readout.inBay &&
    readout.speed < cfg.dock.maxSpeed &&
    Math.abs(readout.alignDeg) <= maxDeg &&
    Math.abs(readout.rollDeg) <= maxDeg
  )
}

/**
 * Advances the dock hold by `dt` seconds: `readout.holdProgress` (0..1 of `dock.holdSeconds`) grows while the
 * dock conditions hold and resets to 0 when any of them fails. Returns true on the step that completes the hold
 * (and on every later step while it stays complete).
 */
export function stepDockHold(readout: DockingReadout, dt: number, cfg: Pick<DockingConfig, 'dock'>): boolean {
  if (!dockConditionsMet(readout, cfg)) {
    readout.holdProgress = 0
    return false
  }
  const p = readout.holdProgress + dt / cfg.dock.holdSeconds
  if (p >= 1 - HOLD_EPS) {
    readout.holdProgress = 1
    return true
  }
  readout.holdProgress = p
  return false
}
